import { $Q, $Qll } from '../utils/query-selector';
import api from '../services/api';
import { stringToHTML } from '../utils/to-html';
import { priceVat } from './price-vat';
import { rewardPoints } from './rewards-points';
import { multiplyQuantity } from '../components/change-quantity';

/**
 * Captures the HTML section of the product in question and returns data
 *
 * @param {String} handle Data element of the product
 * to which the query will be made
 * @param {String} variantId Id of the selected variant
 * @returns Object - price, available, button
 */
async function sectionHandle(handle, variantId) {
  const locale =
    window.theme.lang === 'fr'
      ? ''
      : window.theme.lang === 'pt-PT'
      ? '/pt'
      : `/${window.theme.lang}`;
  const htmlResponse = await api.shopifyVariantByUrl(
    `${locale}/products/${handle}`,
    variantId
  );

  const variantPrice = $Q('.price-product-js', stringToHTML(htmlResponse));
  const variantAvailable = $Q('[name="available"]', stringToHTML(htmlResponse));
  const button = $Q(
    '.main-product__variants .btn-cart-js:not(.hidden)',
    stringToHTML(htmlResponse)
  );
  const rewardsPoints = $Qll('.points-message', stringToHTML(htmlResponse));
  const stockMessage = $Q('.badge', stringToHTML(htmlResponse));
  const expiryDate = $Q('.freshly-expiry-date', stringToHTML(htmlResponse));
  const defectiveForm = $Q('#defective-form', stringToHTML(htmlResponse));

  return {
    price: variantPrice.outerHTML,
    available: variantAvailable.value,
    button: button,
    rewardsPoints: rewardsPoints,
    stockMessage: stockMessage,
    defectiveForm: defectiveForm?.innerHTML || '',
    expiryDate: expiryDate?.innerHTML || '',
  };
}

/**
 * Inject new price node to the section
 *
 * @param {HTMLCollection} variantPrice - Object with the price value
 * @param {HTMLElement} parent - Parent node to closest
 * with className 'product-js'
 *
 */
export function updatePrice(variantPrice, parent) {
  const sectionPrice = $Q('.price-product-js', parent);

  sectionPrice.innerHTML = variantPrice;

  priceVat();
}

/**
 * Update stock status information
 * @param {Node} stockMessage span badge
 */
function updateStatus(stockMessage, value, parent) {
  if (!parent) return;

  const disponibilityContainer = $Q('.product-disponibility', parent);
  const currentMessage = $Q('.variant-exist', disponibilityContainer);
  const variantNonExist = $Q('.variant-nonexist', disponibilityContainer);

  if (!value) {
    currentMessage.classList.add('hidden');
    variantNonExist?.classList.remove('hidden');
    return;
  }

  variantNonExist?.classList.add('hidden');

  if (disponibilityContainer && currentMessage) {
    currentMessage.remove();
    disponibilityContainer.appendChild(stockMessage);
  }
}

/**
 * Injectnew rewards points node to the section
 *
 * @param {HTMLCollection} variantPrice - Object with the price value
 * @param {HTMLElement}
 * parent - Parent node to closest with className 'product-js'
 *
 */
function updateRewardsPoints(rewardsPoints) {
  const sectionPoints = $Qll('.points-message');

  sectionPoints.forEach((element, i) => {
    sectionPoints[i].innerHTML = rewardsPoints[i].innerHTML;
  });

  rewardPoints();
}

/**
 * Show not available of the variant, depending of the stock
 *
 * @param {String} available - Dataset available
 * @param {HTMLElement} parent - Parent node to closest
 * with className 'product-js'
 * @param {String} newText - New text in button add to cart
 */
function updateButton(available, parent, currentButton, value) {
  $Qll('.btn-cart-js', parent).forEach((button) => {
    if (currentButton.name === button.name) {
      button.innerHTML = currentButton.textContent;
      button.classList.remove('hidden');
      button.disabled = available === 'false' && !value;
    } else {
      button.classList.add('hidden');
    }
  });
}

/**
 * Section rendering to dynamic price and available data
 *
 * @param {HTMLElement} param0 - Node with event change
 *
 * @author Andres Briñez
 * @author Cristian Velasco
 * @version 2.0
 */
export async function queryVariants({ target }) {
  const addcartBtn = $Q(
    '.btn-cart-js:not(.hidden)',
    target.closest('.product-js')
  );
  const { value, dataset } = $Q('[name="id"]', target.closest('.product-js'));
  const productPage = $Q('.main-product__detail')
    ? $Q('.main-product__detail')
    : document;
  const quantityInput = $Q(
    '.main-product__variants .main-product__add-actions .quantity__container input'
  );

  addcartBtn.disabled = true;
  addcartBtn.innerHTML = '<div id="loading"></div>';

  const {
    price,
    available,
    button,
    rewardsPoints,
    stockMessage,
    defectiveForm,
    expiryDate,
  } = await sectionHandle(dataset.variant, value);

  const productDetailContainer = target.closest('.main-product__detail');

  if ($Q('.freshly-expiry-date', productDetailContainer))
    $Q('.freshly-expiry-date', productDetailContainer).innerHTML =
      expiryDate || '';
  $Q('#defective-form').innerHTML = defectiveForm;
  updateStatus(stockMessage, value, productDetailContainer);
  updatePrice(price, target.closest('.product-js'));
  updateButton(available, target.closest('.product-js'), button, value);
  window.backInStockRegisterEvents();
  multiplyQuantity(productPage, quantityInput.value);

  if (rewardsPoints != null) {
    updateRewardsPoints(rewardsPoints);
  }

  if (!value) {
    addcartBtn.disabled = true;
    $Q('.main-product__detail').classList.add('hide-defective-form');
  } else {
    $Q('.main-product__detail').classList.remove('hide-defective-form');
  }
}
